/**
 * O funil e o que sobra dele.
 *
 * O funil é a razão de esta tela existir: filtro que nunca corta nada é filtro
 * que ninguém revisou, e isso só aparece quando o corte de cada etapa está
 * visível ao lado do que restou. Mexer num valor em Configuração muda estes
 * números na volta — é o laço que fecha a ideia de configuração como dado.
 *
 * O DESENHO (20/10, "simples e clean"): o funil em etapas lado a lado, cada
 * uma com o que restou e o que cortou; e as licitações como linhas — o
 * prazo num bloco à esquerda, o objeto e o órgão no meio, o valor e a UF à
 * direita. Era uma tabela de seis colunas com o objeto quebrando em dez linhas.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): as seções, a nota, a lista, a linha, as
 * etiquetas e os avisos saem de `src/ds`. Em `gestao.module.css` ficou o que é
 * desta tela: o funil em setas, o bloco do prazo e o valor.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api/cliente";
import { AreaConfig } from "../componentes/AreaConfig";
import { Aviso, Etiqueta, ItemDeLista, Lista, Nota, Secao } from "../ds";
import { valorCurto } from "../formato";
import css from "./gestao.module.css";

export function Fila() {
  const funil = useQuery({ queryKey: ["funil"], queryFn: api.funil });
  // A mesma consulta de Oportunidades, sem filtro de tela: aqui interessa o
  // que o FUNIL deixou passar, não o recorte que alguém escolheu.
  const fila = useQuery({
    queryKey: ["fila"],
    queryFn: () => api.listar({ ordem: "prazo" }, 0, 200),
  });

  return (
    <AreaConfig
      titulo="Funil de seleção"
      descricao={
        <>
          De tudo o que foi encontrado, quantas licitações passam por cada filtro
          até chegar à avaliação. Os filtros se ajustam em{" "}
          <Link to="/configuracao">Regras e ajustes</Link>.
        </>
      }
    >
      <Secao titulo="Quantas sobram a cada filtro">
        {funil.isPending && <Aviso simples>Calculando o funil…</Aviso>}
        {funil.data && funil.data.length > 0 && (
          // O FUNIL COMO UMA FRASE DE NÚMEROS (20/10, "são dados, é simples —
          // seja minimalista"): cada etapa é um número grande com o nome
          // embaixo, e a seta entre duas diz quanto a etapa cortou. Sem caixa,
          // sem barra, sem tabela. O último número, o que chega à fila, é o
          // único em azul.
          <>
            <ol className={css.fluxo}>
              {funil.data.map((d, i) => {
                const { nome, regra, corte } = legivel(d.etapa);
                const ultimo = i === funil.data.length - 1;
                return (
                  <li key={d.etapa} className={css.fluxoPasso}>
                    {i > 0 && (
                      <span className={css.fluxoSeta} aria-hidden="true">
                        <small data-zero={d.cortou === 0}>
                          {d.cortou > 0 ? `−${d.cortou.toLocaleString("pt-BR")}` : "nenhuma"}
                        </small>
                        <svg viewBox="0 0 48 10" preserveAspectRatio="none">
                          <path d="M0 5h46M42 1l4 4-4 4" />
                        </svg>
                        {corte && <small className={css.fluxoMotivo}>{corte}</small>}
                      </span>
                    )}
                    <span className={css.fluxoMarco} data-fim={ultimo}>
                      <b>{d.restam.toLocaleString("pt-BR")}</b>
                      <span className={css.fluxoNome}>{nome}</span>
                      {regra && <small>{regra}</small>}
                      {i > 0 && (
                        <span className="apenas-leitor">
                          {d.cortou > 0
                            ? `, cortou ${d.cortou.toLocaleString("pt-BR")}`
                            : ", não cortou nada"}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ol>
            <Nota>
              {Math.round(
                ((funil.data[funil.data.length - 1]?.restam ?? 0) / (funil.data[0]?.restam || 1)) * 100,
              )}
              % das licitações encontradas passam por todos os filtros e seguem
              para a avaliação.
            </Nota>
          </>
        )}
      </Secao>

      <Secao titulo="Aguardando avaliação" conta={fila.data?.total}>
        {fila.isPending && <Aviso simples>Lendo a fila…</Aviso>}
        {fila.data?.total === 0 && (
          <Aviso simples>
            Nada passou pelos filtros. Afrouxe um valor em Configuração, ou colete
            de novo.
          </Aviso>
        )}
        {!!fila.data?.itens.length && (
          <Lista>
            {fila.data.itens.map((l) => (
              <ItemDeLista
                key={l.id}
                className={css.comPrazo}
                href={l.url_origem ?? undefined}
                externo
                title={l.url_origem ? "Abre o anúncio no portal de origem" : undefined}
                inicio={
                  <span className={css.prazo} title="Encerramento das propostas">
                    {l.encerramento ? (
                      <>
                        <b>{dia(l.encerramento)}</b>
                        <small>{mes(l.encerramento)}</small>
                      </>
                    ) : (
                      <small>sem prazo</small>
                    )}
                  </span>
                }
                texto={l.objeto}
                meta={<span>{l.orgao ?? "Órgão não informado"}</span>}
                fim={
                  <>
                    <b className={css.valor}>{valorCurto(l.valor_estimado) ?? "sem valor"}</b>
                    <span className={css.mudo}>{l.uf ?? "—"}</span>
                  </>
                }
              >
                {l.termos.length > 0 && (
                  <span className={css.etiquetas}>
                    {l.termos.map((p) => (
                      <Etiqueta key={p}>{p}</Etiqueta>
                    ))}
                  </span>
                )}
              </ItemDeLista>
            ))}
          </Lista>
        )}
      </Secao>
    </AreaConfig>
  );
}

/**
 * A ETAPA NAS PALAVRAS DE QUEM DECIDE (20/10, "o meu chefe não consegue
 * entender nada disso"). O servidor nomeia a etapa pelo filtro técnico —
 * "prazo (nao venceu)", "regra 1 (score 50 ou mais…)" —; aqui ela vira o que
 * a licitação que passou TEM ("Ainda abertas") e o que a cortada tinha ("já
 * encerradas"). A chave é a primeira palavra do servidor; o que vem entre
 * parênteses continua aparecendo quando diz um número que a frase fixa não
 * sabe (os dias, os termos, as UFs). Etapa nova cai no texto do servidor.
 */
function legivel(etapa: string): { nome: string; regra: string | null; corte: string | null } {
  const m = etapa.match(/^([^(]+?)\s*(?:\((.+)\))?\s*$/);
  const chave = (m?.[1] ?? etapa).trim().toLowerCase();
  const dentro = m?.[2] ?? null;
  if (chave === "acervo") {
    return { nome: "Encontradas", regra: "todas as licitações já coletadas", corte: null };
  }
  if (chave === "prazo") {
    const dias = dentro?.match(/(\d+)\s*d/)?.[1];
    return {
      nome: "Ainda abertas",
      regra: dias ? `o prazo de proposta acaba em mais de ${dias} dias` : "o prazo de proposta não venceu",
      corte: "já encerradas",
    };
  }
  if (chave === "situacao" || chave === "situação") {
    return {
      nome: "Em andamento",
      regra: "não estão suspensas, revogadas, anuladas nem canceladas",
      corte: "suspensas ou canceladas",
    };
  }
  if (chave === "negativos") {
    return { nome: "Sem palavra excluída", regra: dentro, corte: "com palavra excluída" };
  }
  if (chave === "valor") {
    return { nome: "Na faixa de valor", regra: dentro, corte: "fora da faixa de valor" };
  }
  if (chave === "regiao" || chave === "região") {
    return { nome: "Na região", regra: dentro, corte: "fora da região" };
  }
  if (chave.startsWith("regra 1")) {
    const piso = dentro?.match(/(\d+)/)?.[1] ?? "50";
    return {
      nome: "Com potencial",
      regra: `nota ${piso} ou mais, ou ainda sem nota`,
      corte: `nota abaixo de ${piso}`,
    };
  }
  const nome = (m?.[1] ?? etapa).trim();
  return { nome: nome.charAt(0).toUpperCase() + nome.slice(1), regra: dentro, corte: null };
}

const dia = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit" });
const mes = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
